import React, { useEffect, useRef, useState, useCallback } from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import { Alert, Chip, Dropdown, Menu, MenuButton, Select, Textarea } from "@mui/joy";
import Option from "@mui/joy/Option";
import Autocomplete from "@mui/joy/Autocomplete";
import AutocompleteOption from "@mui/joy/AutocompleteOption";
import ListItemContent from "@mui/joy/ListItemContent";
import Tooltip from '@mui/joy/Tooltip';
import { ArrowDropDown, Block, InfoOutlined, PhoneOutlined } from "@mui/icons-material";
import Button from "@mui/joy/Button";
import { toast, ToastContainer } from "react-toastify";

import ApiService from "../../axios/Bookings-Axios";
import GeneralApiService, {
    GetAddressFromPostcode,
    GetUser,
    PostSMSTextResponse,
} from "../../axios/Axios";
import { useCall } from "../../lib/call-context";

interface User {
    username: string;
}

interface Customer {
    id: number;
    full_name: string;
    email: string;
    phone: string;
    street_address: string;
    postcode: string;
    house_type?: { name: string; id: number };
    notes?: string;
    advertisement_platform?: any;
    created_at?: string;
    unsubscribed?: boolean;
    franchise_postcode?: {
        franchise: { id: number; location: string; owner?: string; phone?: string; email?: string; };
        service_price_tier?: any;
    };
}

interface SingleAddressSuggestion {
    address: string;
    url: string;
    id: string;
}

interface CustomersTopComponentProps {
    customer: Customer;
    setCustomer: React.Dispatch<React.SetStateAction<Customer>>;
}

export default function CustomersTopComponent({ customer, setCustomer }: CustomersTopComponentProps) {
    const [newCustomer, setNewCustomer] = useState<boolean>(() => !customer || customer.id === 0);
    const [invalidPostcode, setInvalidPostcode] = useState<boolean>(false);
    const [searchedCustomers, setSearchedCustomers] = useState<Customer[]>([]);
    const [validOutwardPostcodes, setValidOutwardPostcodes] = useState<string[]>([]);
    const searchDebounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [user, setUser] = useState<User>({ username: '' });
    const [text, setText] = useState('');
    const [sendingText, setSendingText] = useState<boolean>(false);
    const [addressSuggestions, setAddressSuggestions] = useState<string[]>([]);
    const [isFetchingAddresses, setIsFetchingAddresses] = useState<boolean>(false);
    const postcodeDebounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const franchiseLookupTimeout = useRef<NodeJS.Timeout | null>(null);

    const [templatedTexts, setTemplatedTexts] = useState<TemplatedText[]>([]);
    const [isFetchingTemplates, setIsFetchingTemplates] = useState<boolean>(false);
    const [streetAddressOpen, setStreetAddressOpen] = useState<boolean>(false);
    const { makeCall } = useCall();

    const getOutwardPostcode = (postcode: string): string | null => {
        const compactPostcode = postcode.trim().toUpperCase().replace(/\s+/g, '');
        const match = compactPostcode.match(/^(.+)\d[A-Z]{2}$/i);
        return match?.[1] ?? null;
    };

    const isPostcodeValid = useCallback((outwardPostcode: string): boolean => {
        if (!outwardPostcode) return true;
        const outwardCode = getOutwardPostcode(outwardPostcode);
        return outwardCode !== null && validOutwardPostcodes.length > 0 && validOutwardPostcodes.includes(outwardCode);
    }, [validOutwardPostcodes]);

    const fetchAddresses = useCallback(async (postcodeToFetch: string) => {
        if (!postcodeToFetch || postcodeToFetch.trim().length < 3) {
            setAddressSuggestions([]);
            return;
        }
        setIsFetchingAddresses(true);
        setAddressSuggestions([]);
        try {
            const response = await GetAddressFromPostcode(postcodeToFetch.trim());
            if (response && response.suggestions && Array.isArray(response.suggestions)) {
                setAddressSuggestions(response.suggestions.map((s: SingleAddressSuggestion) => s.address));
            } else {
                setAddressSuggestions([]);
            }
        } catch (error) {
            console.error("Error fetching addresses:", error);
            setAddressSuggestions([]);
        } finally {
            setIsFetchingAddresses(false);
        }
    }, []);

    useEffect(() => {
        if (customer && customer.id !== 0) {
            setNewCustomer(false);
        }
    }, [customer]);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [userResponse, franchisePostcodesResponse] = await Promise.all([
                    GetUser(),
                    GeneralApiService.getAllFranchisePostcodes()
                ]);
                setUser(userResponse.data);
                if (franchisePostcodesResponse && franchisePostcodesResponse.data) {
                    setValidOutwardPostcodes(franchisePostcodesResponse.data.map((fp: any) => fp.outward_postcode.toUpperCase()));
                }
            } catch (error) {
                console.error("Error fetching initial data:", error);
            }
        }
        fetchInitialData();
    }, []);

    useEffect(() => {
        const location = localStorage.getItem('location');
        if(location?.toLowerCase() != 'all'){
            setInvalidPostcode(customer?.postcode ? !isPostcodeValid(customer.postcode) : false);
        }
    }, [customer?.postcode, isPostcodeValid]);

    useEffect(() => {
        if (!newCustomer && customer?.postcode && customer.postcode.trim().length >= 3) {
            fetchAddresses(customer.postcode);
        } else if (!newCustomer) {
            setAddressSuggestions([]);
        }
        if (newCustomer) {
            setAddressSuggestions([]);
        }
    }, [customer?.postcode, newCustomer, fetchAddresses]);

    const searchCustomers = (event: any, value: string) => {
        if (searchDebounceTimeout.current) clearTimeout(searchDebounceTimeout.current);
        if (!value || value.trim() === "") {
            setSearchedCustomers([]);
            return;
        }
        searchDebounceTimeout.current = setTimeout(async () => {
            try {
                const customersData = await ApiService.getCustomers({ page: -1, search: value });
                setSearchedCustomers(Array.isArray(customersData) ? customersData : []);
            } catch (error) {
                console.error("Error searching customers:", error);
                setSearchedCustomers([]);
            }
        }, 300);
    };

    const callCustomer = async () => {
        if (customer && customer.phone) {
            makeCall(customer.phone, customer.full_name);
        } else {
            toast.warn('Customer phone number is missing.', { containerId: "customers-top-component" });
        }
    };

    const sendTextToCustomer = () => {
        if (!customer?.phone) { toast.warn('Customer phone number is missing.', { containerId: "customers-top-component" }); return; }
        if (!text.trim()) { toast.warn('SMS message cannot be empty.', { containerId: "customers-top-component" }); return; }
        setSendingText(true);
        const newChatMessage = {
            sender: { id: '-1', name: user.username || "System", phone: customer.phone, isCustomer: false },
            content: text,
            timestamp: new Date().toISOString(),
        };
        PostSMSTextResponse(newChatMessage)
            .then(sendTextResponse => {
                if (sendTextResponse?.data?.sent) {
                    toast.success('SMS Sent!', { containerId: "customers-top-component" });
                    setText('');
                } else {
                    toast.warn(sendTextResponse?.data?.message || 'SMS not sent.', { containerId: "customers-top-component" });
                }
            })
            .catch(error => {
                console.error("Error sending SMS:", error);
                toast.error('Failed to send SMS.', { containerId: "customers-top-component" });
            })
            .finally(() => setSendingText(false));
    };

    const lookupFranchiseFromPostcode = useCallback((postcode: string) => {
        const outwardCode = getOutwardPostcode(postcode);
        if (!outwardCode) {
            return;
        }
        GeneralApiService.lookupFranchisePostcode(outwardCode)
            .then((data: any) => {
                setCustomer(prev => ({
                    ...prev,
                    franchise_postcode: {
                        franchise: data.franchise,
                        service_price_tier: data.service_price_tier,
                    }
                } as Customer));
            })
            .catch(() => {
                setCustomer(prev => ({ ...prev, franchise_postcode: undefined } as Customer));
            });
    }, [setCustomer]);

    const handleCustomerInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setCustomer(prev => ({ ...prev, [name]: value } as Customer));
        if (name === 'postcode') {
            if (postcodeDebounceTimeout.current) clearTimeout(postcodeDebounceTimeout.current);
            if (franchiseLookupTimeout.current) clearTimeout(franchiseLookupTimeout.current);
            const currentPostcode = value.trim();
            if (currentPostcode) {
                postcodeDebounceTimeout.current = setTimeout(() => {
                    fetchAddresses(currentPostcode);
                    setCustomer(prev => ({ ...prev, street_address: '' } as Customer));
                    setStreetAddressOpen(true);
                }, 750);
                franchiseLookupTimeout.current = setTimeout(() => {
                    lookupFranchiseFromPostcode(currentPostcode);
                }, 750);
            } else {
                setAddressSuggestions([]);
                setCustomer(prev => ({ ...prev, franchise_postcode: undefined } as Customer));
            }
        }
    };

    const handleCustomerHouseTypeChange = (newValue: string | null) => {
        if (newValue !== null) {
            setCustomer(prev => ({ ...prev, house_type: { name: newValue, id: -1 } } as Customer));
        }
    };

    const createEmptyCustomer = (): Customer => ({
        id: 0, full_name: "", email: "", phone: "", street_address: "",
        postcode: "", house_type: { name: 'Detached', id: -1 }, notes: "",
    });

    function clearCustomerAndSwitchToNew() {
        setNewCustomer(false);
        setCustomer(createEmptyCustomer());
        setAddressSuggestions([]);
        setInvalidPostcode(false);
        setSearchedCustomers([]);
    }

    function selectExistingCustomer(event: any, selectedValue: Customer | null) {
        if (selectedValue) {
            setCustomer(selectedValue);
        } else {
            clearCustomerAndSwitchToNew();
        }
    }

    const handleOpenSmsMenu = async () => {
        if (!customer?.id || templatedTexts.length > 0) {
            return;
        }
        setIsFetchingTemplates(true);
        try {
            const response = await GeneralApiService.getAllTemplatedTexts(customer.id);
            setTemplatedTexts(response.data || []);
        } catch (error) {
            console.error("Failed to fetch templated texts:", error);
            toast.error("Could not load SMS templates.", { containerId: "customers-top-component" });
            setTemplatedTexts([]); // Clear on error
        } finally {
            setIsFetchingTemplates(false);
        }
    };

    const currentCustomer = customer || createEmptyCustomer();

    return (
        <Box>
            <ToastContainer containerId={"customers-top-component"} position="top-right" autoClose={5000} theme="dark" />
            <Typography variant="soft" sx={{ mb: '5px', width: '100%' }}>Customer Information</Typography>

            {newCustomer ? (
                <Box sx={{ width: '100%', marginTop: '10px' }}>
                    <FormLabel>Search For An Existing Customer</FormLabel>
                    <Autocomplete
                        autoHighlight
                        options={searchedCustomers}
                        getOptionLabel={(option: Customer) => option.full_name || ''}
                        onInputChange={searchCustomers}
                        onChange={selectExistingCustomer}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        renderOption={(props, option: Customer) => (
                            <AutocompleteOption {...props} key={option.id}>
                                <ListItemContent sx={{ fontSize: 'sm' }}>
                                    {option.full_name}
                                    <Typography level="body-xs">
                                        ({option.street_address || 'No address'}) {option.phone || 'No phone'}
                                    </Typography>
                                </ListItemContent>
                            </AutocompleteOption>
                        )}
                        placeholder="Search by name, street, phone or email"
                        noOptionsText="No customers found"
                    />
                    <Typography onClick={clearCustomerAndSwitchToNew} sx={{ cursor: 'pointer', marginTop: 1, marginBottom: 1, textDecoration: 'underline' }} color="primary">
                        Or click here to create a NEW customer
                    </Typography>
                </Box>
            ) : (
                <>
                    {currentCustomer.id !== 0 && currentCustomer.full_name && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography>Editing Customer: <Typography fontWeight={500}>{currentCustomer.full_name}</Typography></Typography>
                                {currentCustomer.unsubscribed && (
                                    <Chip color="danger" variant="soft" size="sm" startDecorator={<Block sx={{ fontSize: 14 }} />}>
                                        Opted Out
                                    </Chip>
                                )}
                            </Box>
                            <Box>
                                <Button onClick={() => setNewCustomer(true)} sx={{ mr: 1 }} color="neutral" variant="outlined">Find/New</Button>
                                <Button onClick={callCustomer} sx={{ mr: 1 }} color="primary" variant="outlined" disabled={!currentCustomer.id} endDecorator={<PhoneOutlined />}>Call</Button>
                                <Dropdown>
                                    <MenuButton
                                        onClick={handleOpenSmsMenu}
                                        color="primary"
                                        disabled={!currentCustomer.id || !currentCustomer.phone || currentCustomer.unsubscribed}
                                        endDecorator={<ArrowDropDown />}
                                    >
                                        {currentCustomer.unsubscribed ? 'Opted Out' : 'Send SMS'}
                                    </MenuButton>

                                    <Menu
                                        sx={{ zIndex: 7700, padding: '10px', width: '300px' }}
                                        color="primary"
                                    >
                                        <FormLabel>Send SMS to {currentCustomer.full_name}</FormLabel>

                                        <Box sx={{ mt: 1.5, mb: 1.5 }}>
                                            <FormLabel sx={{ mb: 0.5 }}>Select a Template</FormLabel>
                                            <Select
                                                value={null}
                                                slotProps={{ listbox: { disablePortal: true } }}
                                                placeholder="Choose a template..."
                                                onChange={(event, newValue) => setText(newValue || '')}
                                            >
                                                {templatedTexts.map((template) => (
                                                    <Option key={template.id} value={template.text}>
                                                        {template.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Box>

                                        <Textarea
                                            value={text}
                                            onChange={(event) => setText(event.target.value)}
                                            endDecorator={
                                                <Box sx={{ display: 'flex', gap: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider', flex: 'auto', alignItems: 'center' }}>
                                                    <Typography level="body-xs" sx={{ flexGrow: 1 }}>{text.length} character(s)</Typography>
                                                    <Button onClick={sendTextToCustomer} loading={sendingText} size="sm" color="primary">Send</Button>
                                                </Box>
                                            }
                                            minRows={3}
                                            placeholder="...or type a custom message here"
                                            sx={{ mt: 1 }}
                                        />
                                    </Menu>
                                </Dropdown>
                            </Box>
                        </Box>
                    )}
                    {currentCustomer.unsubscribed && (
                        <Alert 
                            color="danger" 
                            variant="soft" 
                            startDecorator={<Block />}
                            sx={{ mb: 2 }}
                        >
                            This customer has opted out and cannot receive marketing messages.
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%', mt: 1 }}>
                        <Box sx={{ width: 'calc(50% - 8px)' }}><FormLabel>Name</FormLabel><Input required name="full_name" value={currentCustomer.full_name || ''} onChange={handleCustomerInputChange} variant="outlined" /></Box>
                        <Box sx={{ width: 'calc(50% - 8px)' }}><FormLabel>E-Mail</FormLabel><Input type="email" name="email" value={currentCustomer.email || ''} onChange={handleCustomerInputChange} variant="outlined" /></Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%', mt: 2 }}>
                        <Box sx={{ width: 'calc(50% - 8px)' }}><FormLabel>House Type</FormLabel>
                            <Select name="house_type" value={currentCustomer.house_type?.name || "Detached"} onChange={(e, newValue) => handleCustomerHouseTypeChange(newValue)} variant="outlined">
                                <Option value={"Detached"}>Detached</Option>
                                <Option value={"Semi Detached"}>Semi-Detached</Option>
                                <Option value={"Terrace"}>Terrace</Option>
                                <Option value={"Bungalow"}>Bungalow</Option>
                                <Option value={"Ground Floor Apartment"}>Ground Floor Apartment</Option>
                                <Option value={"1st Floor Apartment"}>1st Floor Apartment</Option>
                                <Option value={"2nd Floor Apartment"}>2nd Floor Apartment</Option>
                                <Option value={"3rd Floor Apartment"}>3rd Floor Apartment</Option>
                            </Select>
                        </Box>
                        <Box sx={{ width: 'calc(50% - 8px)' }}><FormLabel>Phone</FormLabel><Input required name="phone" type="tel" value={currentCustomer.phone || ''} onChange={handleCustomerInputChange} variant="outlined" /></Box>
                    </Box>
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <FormLabel>Postcode</FormLabel>
                        <Input required name="postcode" value={currentCustomer.postcode || ''} onChange={handleCustomerInputChange} variant="outlined" sx={{ mb: invalidPostcode ? 0 : 1 }} />
                        {invalidPostcode && (<Typography sx={{ mt: '5px', mb: '10px' }} startDecorator={<Tooltip title="The postcode you have entered is not a valid postcode for this franchise. Please make sure the postcode is located in the same place as the franchise."><InfoOutlined /></Tooltip>} color="danger" level="body-sm">This postcode is not valid for this franchise.</Typography>)}
                    </Box>
                    <Box sx={{ width: '100%', mt: 1 }}>
                        <FormLabel>Street Address</FormLabel>
                        <Autocomplete
                            freeSolo options={addressSuggestions} getOptionLabel={(option) => option}
                            inputValue={currentCustomer.street_address || ''}
                            open={streetAddressOpen}
                            onOpen={() => setStreetAddressOpen(true)}
                            onClose={() => setStreetAddressOpen(false)}
                            onInputChange={(event, newInputValue, reason) => { if (reason === 'input') setCustomer(prev => ({ ...prev, street_address: newInputValue } as Customer)); }}
                            onChange={(event, newValue: string | null) => {
                                setCustomer(prev => ({ ...prev, street_address: newValue || '' } as Customer));
                                setStreetAddressOpen(false);
                            }}
                            loading={isFetchingAddresses} loadingText="Loading addresses..."
                            noOptionsText={currentCustomer.postcode && currentCustomer.postcode.length >= 3 && !isFetchingAddresses ? "No suggestions found for this postcode." : "Enter postcode for suggestions"}
                            fullWidth placeholder="Type or select street address"
                            slotProps={{ input: { required: true, variant: "outlined" } }}
                            renderOption={(props, option) => (<AutocompleteOption {...props} key={option + Math.random()}>{option}</AutocompleteOption>)}
                        />
                    </Box>
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <FormLabel>Customer Notes</FormLabel>
                        <Textarea name="notes" value={currentCustomer.notes || ''} onChange={handleCustomerInputChange} variant="outlined" minRows={3} maxRows={3} />
                    </Box>
                </>
            )}
        </Box>
    );
}